import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { AlertTriangleIcon, BookOpenIcon, ArchiveIcon } from 'lucide-react';
type WarningBlock = {
  icon: React.ReactNode;
  title: string;
  description: string;
};
const blocks: WarningBlock[] = [{
  icon: <AlertTriangleIcon className="w-7 h-7" data-id="element-253" />,
  title: 'IDENTIFICA LAS SEÑALES',
  description: 'Aprende a reconocer los mecanismos de control social antes de que se vuelvan irreversibles.'
}, {
  icon: <BookOpenIcon className="w-7 h-7" data-id="element-254" />,
  title: 'EL MANUAL DEL DESMANTELAMIENTO',
  description: 'Cómo una potencia petrolera se quedó sin combustible y su moneda se volvió polvo.'
}, {
  icon: <ArchiveIcon className="w-7 h-7" data-id="element-255" />,
  title: 'MEMORIA CONTRA EL OLVIDO',
  description: 'Dejamos constancia para que el mundo no pueda decir que no sabía. Para que no vuelva a ocurrir.'
}];
export function WarningSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full py-14 md:py-28 px-6 relative overflow-hidden" style={{
    background: 'linear-gradient(180deg, #050505 0%, #0a0805 50%, #050505 100%)'
  }} data-id="element-256">
      {/* Subtle gold glow */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(ellipse at 50% 0%, #CF9B2E 0%, transparent 60%)'
    }} data-id="element-257" />

      <div className="relative z-10 max-w-5xl mx-auto" data-id="element-258">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-259">
          <p className="text-[#CF9B2E] uppercase tracking-[0.3em] text-xs font-bold mb-5 font-['Lato']" data-id="element-260">
            Más allá de Venezuela
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#F5F0E8] mb-8 leading-[1.05] tracking-tight" style={{
          fontFamily: 'Playfair Display, serif'
        }} data-id="element-261">
            UNA ADVERTENCIA
            <br data-id="element-262" />
            <span className="text-[#CF9B2E]" data-id="element-263">PARA EL MUNDO</span>
          </h2>
          <div className="gold-divider mb-8" data-id="element-264" />
          <p className="text-[#9A9080] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" data-id="element-265">
            Venezuela no siempre fue sinónimo de crisis. Durante décadas, fue la
            promesa cumplida de América Latina. Este libro no solo registra el
            dolor de quienes se fueron, sino que expone la secuencia de
            decisiones que pueden destruir a cualquier nación.
          </p>
        </motion.div>

        {/* Three blocks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-266">
          {blocks.map((block, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.15 + index * 0.12
        }} className="bg-[#0a0a0a] p-8 rounded-sm relative group transition-all duration-300 hover:bg-[#0d0905]" style={{
          border: '1px solid rgba(207, 155, 46, 0.25)',
          boxShadow: '0 0 0 1px rgba(207, 155, 46, 0.05) inset'
        }} data-id="element-267">
              {/* Subtle gold corner accent */}
              <div className="absolute top-0 left-0 w-12 h-px" style={{
            background: 'linear-gradient(90deg, #CF9B2E 0%, transparent 100%)'
          }} data-id="element-268" />
              <div className="absolute top-0 left-0 w-px h-12" style={{
            background: 'linear-gradient(180deg, #CF9B2E 0%, transparent 100%)'
          }} data-id="element-269" />

              <div className="text-[#CF9B2E] mb-5" data-id="element-270">{block.icon}</div>
              <h3 className="text-[#F5F0E8] font-bold text-sm tracking-[0.08em] mb-4 leading-snug font-['Lato']" style={{
            letterSpacing: '0.05em'
          }} data-id="element-271">
                {block.title}
              </h3>
              <p className="text-[#9A9080] text-[15px] leading-relaxed" data-id="element-272">
                {block.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}